/**
 * BaseDialog – reusable modal dialog built on Material Tailwind Dialog.
 * Overlay click does NOT close the dialog. Only Cancel button, Escape key, or onClose callback close it.
 * @see https://www.material-tailwind.com/docs/react/dialog
 */

import React from 'react'
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from '@material-tailwind/react'

export interface IBaseDialogProps<T1 extends string | React.ReactNode = string, T2 = string> {
    /** Whether the dialog is visible */
    showDialog: boolean
    /** Called when the dialog should close (Cancel, Escape, or programmatic) */
    onClose?: () => void
    /** Called when Save is clicked */
    onSave?: () => void

    /** Unique identifier for the dialog */
    id?: string
    /** Content rendered in the header area */
    headerContent?: React.ReactNode | string
    /** Content rendered in the body area */
    bodyContent?: React.ReactNode | string
    /** Custom content rendered in the footer (replaces default Save/Cancel buttons) */
    footerContent?: React.ReactNode | string
    /** Custom label for the save button (default: "Save") */
    saveLabel?: string
    /** Custom label for the cancel button (default: "Cancel") */
    cancelLabel?: string
    /** Hide the footer entirely */
    hideFooter?: boolean
    /** Hide the save button (cancel still visible) */
    saveHidden?: boolean
    /** Disable the save button */
    saveDisabled?: boolean
    /** Additional Tailwind classes on the content panel */
    className?: string
    /** Dialog size preset */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
}

export function BaseDialog({
    id,
    showDialog,
    onClose,
    onSave,
    headerContent,
    bodyContent,
    footerContent,
    saveLabel = 'Save',
    cancelLabel = 'Close',
    hideFooter = false,
    saveHidden = false,
    saveDisabled = false,
    className = '',
    size = 'md',
}: IBaseDialogProps) {
    const [currentShowDialog, setCurrentShowDialog] = React.useState(showDialog)

    React.useEffect(() => {
        setCurrentShowDialog(showDialog)
    }, [showDialog])

    const handleClose = () => {
        setCurrentShowDialog(false)
        onClose?.()
    }

    return (
        <Dialog
            open={currentShowDialog}
            handler={handleClose}
            size={size}
            dismiss={{ outsidePress: false }}
            className={`bg-background text-foreground text-2xl px-6 pt-4 pb-3 rounded-3xl ${className}`}
            animate={{
                mount: { scale: 1, y: 0, transition: { duration: 0.15 } },
                unmount: { scale: 0.9, y: -100, transition: { duration: 0.15 } },
            }}
        >
            {/* Header */}
            {headerContent && (
                <DialogHeader className="border-b border-border text-foreground !text-4xl">
                    {headerContent}
                </DialogHeader>
            )}

            {/* Body */}
            <DialogBody className="overflow-auto max-h-[65vh] !text-2xl">
                {bodyContent}
            </DialogBody>

            {/* Footer */}
            {!hideFooter && (
                <DialogFooter className="flex items-center justify-end gap-3 border-t border-border">
                    {footerContent ?? (
                        <>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center px-5 py-2.5 text-2xl font-medium
                                    rounded-lg border border-border bg-muted text-foreground
                                    hover:bg-muted/80 transition-colors cursor-pointer"
                                onClick={handleClose}
                            >
                                {cancelLabel}
                            </button>
                            {!saveHidden && (
                                <button
                                    type="button"
                                    className={`inline-flex items-center justify-center px-5 py-2.5 text-2xl font-medium
                                        rounded-lg bg-primary text-primary-foreground
                                        hover:bg-primary/90 transition-colors cursor-pointer
                                        ${saveDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={onSave}
                                    disabled={saveDisabled}
                                >
                                    {saveLabel}
                                </button>
                            )}
                        </>
                    )}
                </DialogFooter>
            )}
        </Dialog>
    )
}
